<template>
    <component :is="embedded ? 'div' : BaseCard" v-bind="embedded ? {} : { theme: 'cream', radius: 'lgr', padding: 'sm' }" class="w-full min-w-0 max-w-150">
        <div v-if="!form && !embedded" class="flex flex-wrap items-center gap-3">
            <BaseAvatar shape="square" size="md"><i class="bi bi-people-fill" aria-hidden="true"></i></BaseAvatar>
            <h3 class="nadia-chat-bold min-w-0 flex-1 text-ink">{{ t("teamPerspective.title") }}</h3>
            <BaseButton theme="neutral" size="sm" class="base-btn-with-icon base-btn-icon-start" :disabled="loading" @click="loadForm">
                <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
                {{ loading ? t("chat.teamPerspective.formLoading") : t("chat.teamPerspective.createTeam") }}
            </BaseButton>
        </div>
        <p v-if="embedded" role="status" class="body-small text-ink-muted empty:sr-only">
            <template v-if="loading">{{ t("chat.teamPerspective.formLoading") }}</template>
        </p>
        <div v-if="loadError" class="mt-3 flex flex-col items-start gap-3">
            <p role="alert" class="body-small text-ink">{{ t("chat.teamPerspective.formLoadError") }}</p>
            <BaseButton v-if="embedded" theme="neutral" size="sm" :disabled="loading" @click="loadForm">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <TeamCreateForm
            v-if="form && !selectedTeam"
            :source="TEAM_INTERACTION_SOURCE.CHAT_TOOL"
            embedded
            class="fs-mask"
            :creator="form.creator"
            :search-url="form.search_url"
            :create-url="form.create_url"
            :team-url-template="form.team_url_template"
            :recommendations-url="form.recommendations_url"
            :back-url="form.back_url"
            :title-shown-above="embedded"
            @submitting="emit('submitting', $event && !selectedTeam)"
            @selected="onSelected" />
        <p class="body text-ink empty:sr-only" role="status">
            <template v-if="selectedTeam">{{ t("peopleDirectory.teamSuggestions.selected", { name: selectedTeam.name }) }}</template>
        </p>
        <div v-if="selectedTeam" class="flex flex-col gap-3">
            <p v-if="selectionError" role="alert" class="body-small text-ink">{{ selectionError }}</p>
            <BaseButton theme="neutral" size="sm" class="self-start" :disabled="disabled" @click="emit('selected', selectedTeam)">
                {{ t("chat.teamPerspective.continueChat") }}
            </BaseButton>
        </div>
    </component>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { TeamCreatePage, TeamPerspectiveChatCard, TeamPerspectiveSummary } from "~vue/api/generated/team-perspective";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import TeamCreateForm from "~vue/Connections/TeamCreateForm.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ formUrl: string; pickerUrl?: string | null; selectionError: string; disabled?: boolean; embedded?: boolean }>();
const emit = defineEmits<{ selected: [team: TeamPerspectiveSummary]; available: [card: TeamPerspectiveChatCard]; submitting: [value: boolean] }>();
const { t } = useI18n();
const form = ref<TeamCreatePage | null>(null);
const loading = ref(false);
const loadError = ref(false);
const selectedTeam = ref<TeamPerspectiveSummary | null>(null);

onMounted(() => {
    if (props.embedded) {
        loadForm();
    }
});

async function loadForm() {
    if (loading.value || form.value) {
        return;
    }
    loading.value = true;
    loadError.value = false;
    try {
        if (props.pickerUrl) {
            const { data, error } = await useFetch(props.pickerUrl).get().json<TeamPerspectiveChatCard>();
            if (error.value || !data.value) {
                loadError.value = true;
                return;
            }
            if (data.value.status !== "no_teams") {
                emit("available", data.value);
                return;
            }
        }
        const { data, error } = await useFetch(props.formUrl).get().json<TeamCreatePage>();
        if (error.value || !data.value) {
            loadError.value = true;
            return;
        }
        form.value = data.value;
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
}

function onSelected(team: TeamPerspectiveSummary) {
    selectedTeam.value = team;
    emit("selected", team);
}
</script>
